/**
 * "Is my voice getting through?" -- the rules, kept free of LiveKit and React
 * so they can be tested on their own. `VoicePathMonitor` gathers the inputs;
 * the mic button's badge and popover show the result.
 *
 * Three checkpoints along the path of your voice:
 *  1. Mic      -- your microphone is producing sound.
 *  2. Network  -- the server says it is receiving your audio (packets sent,
 *                 loss and round trip as the server reports them, and
 *                 LiveKit's own connection quality).
 *  3. Listeners -- every other participant's app tells each speaker whether
 *                 that speaker's audio is arriving, and whether its browser
 *                 lets it play. This is the one that catches "some people
 *                 hear me and some don't".
 *
 * Listener notes are kept small and rare, because their cost grows with the
 * size of the call:
 *  - a note lists only the speakers whose audio is NOT arriving (normally
 *    none), so it stays ~40 bytes however many people are in the call;
 *  - it is sent when what it would say changes, plus a heartbeat every
 *    RECEIPT_HEARTBEAT_MS, not on every sample;
 *  - "not arriving" means an unmuted speaker's packets have not increased for
 *    2 s (outageTracker.ts) -- decided from RTP packet counters, because with
 *    Opus DTX a silent but alive mic still sends a tiny packet every ~400 ms;
 *  - it goes only to participants whose mic is on -- nobody else is waiting
 *    to know who hears them;
 *  - `inaudible` (#2223 AC44/AC45) lists the speakers whose audio arrives but
 *    plays nothing audible here while they talk (lib/audibility.ts). Without
 *    it a listener whose audio arrived but was silent read as "hears you",
 *    because `playing` only says the browser is *allowed* to play. Optional
 *    on the wire, so a note from an app without it still decodes.
 */

/** Data-channel topic for receipts. */
export const VOICE_RECEIPT_TOPIC = "weco.voice-receipt";
/** How often the badge/receipt stats are sampled (locally; no network cost). Outage timing runs on its own 1 s sampler. */
export const VOICE_PATH_INTERVAL_MS = 3000;
/** A listener resends an unchanged note this often, so a speaker can tell it still holds. */
export const RECEIPT_HEARTBEAT_MS = 15_000;
/** A note older than this no longer says anything (two missed heartbeats and change). */
export const RECEIPT_TTL_MS = 40_000;
/** After unmuting, listeners get this long to start receiving before anyone counts as missing. */
export const UNMUTE_GRACE_MS = 6000;
/** No sound from the mic for this long reads as "check your mic" (a hint only; outages use packet counters). */
export const SILENT_AFTER_MS = 15_000;
/** Mic level (0-1) below which the mic counts as producing nothing. */
export const SILENT_LEVEL = 0.002;

/** What a listener tells the speakers. */
export interface VoiceReceipt {
  v: 2;
  /** Speakers (unmuted, as this listener sees them) whose audio is not arriving here. */
  missing: string[];
  /** Whether this browser is allowed to play call audio (autoplay not blocking, sound not at 0). */
  playing: boolean;
  /** Speakers whose audio arrives but nothing audible plays here while they talk (#2223). */
  inaudible?: string[];
}

export function encodeReceipt(receipt: VoiceReceipt): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(JSON.stringify(receipt));
}

export function decodeReceipt(payload: Uint8Array): VoiceReceipt | null {
  try {
    const data = JSON.parse(new TextDecoder().decode(payload)) as Partial<VoiceReceipt> | null;
    if (
      !data ||
      data.v !== 2 ||
      !Array.isArray(data.missing) ||
      !data.missing.every((id) => typeof id === "string") ||
      typeof data.playing !== "boolean"
    ) {
      return null;
    }
    const inaudible =
      Array.isArray(data.inaudible) && data.inaudible.every((id) => typeof id === "string") ? data.inaudible : [];
    return { v: 2, missing: data.missing, playing: data.playing, inaudible };
  } catch {
    return null;
  }
}

/** What a listener observed this sample, before deciding whether to tell anyone. */
export interface ReceiptPlan {
  /** Speakers whose audio is not arriving. */
  missing: string[];
  /** Speakers whose audio is confirmed arriving. Not sent; a change here still
   *  triggers a note, so a speaker who just unmuted hears back promptly. */
  arriving: string[];
  playing: boolean;
  /** Speakers whose audio arrives but is not audible here (lib/audibility.ts). */
  inaudible: string[];
  /** Participants whose mic is on: the only ones the note goes to. */
  destinations: string[];
}

export interface SentReceipt {
  key: string;
  at: number;
}

export function receiptKey(plan: ReceiptPlan): string {
  const sorted = (ids: string[]) => [...ids].sort();
  return JSON.stringify([
    sorted(plan.missing),
    sorted(plan.arriving),
    plan.playing,
    sorted(plan.destinations),
    sorted(plan.inaudible),
  ]);
}

/** Send when there is someone to tell and either something changed or a heartbeat is due. */
export function shouldSendReceipt(plan: ReceiptPlan, last: SentReceipt | null, now: number): boolean {
  if (plan.destinations.length === 0) return false;
  return !last || last.key !== receiptKey(plan) || now - last.at >= RECEIPT_HEARTBEAT_MS;
}

export type MicState = "off" | "muted" | "silent" | "live";
export type NetworkState = "good" | "weak" | "down" | "unknown";
/**
 * hears    -- your audio arrives and their browser plays it;
 * paused   -- it arrives, but their browser has paused the call's sound;
 * inaudible -- it arrives and may play, but nothing audible came out there
 *             while you talked (#2223);
 * missing  -- it is not arriving;
 * unknown  -- no recent report (just joined, or an app without this feature).
 */
export type ListenerState = "hears" | "paused" | "inaudible" | "missing" | "unknown";
export type ConnectionQualityValue = "excellent" | "good" | "poor" | "lost" | "unknown";

export interface NetworkSample {
  connected: boolean;
  micSending: boolean;
  /** Packets sent since the previous sample; null before there is one. */
  packetsSentDelta: number | null;
  /** Percent of sent packets the server reports lost since the previous sample. */
  lossPct: number | null;
  rttMs: number | null;
  quality: ConnectionQualityValue;
}

export function networkState(s: NetworkSample): NetworkState {
  if (!s.connected || s.quality === "lost") return "down";
  // Unmuted but nothing leaving: the audio is not getting out at all.
  if (s.micSending && s.packetsSentDelta === 0) return "down";
  if (s.quality === "poor" || (s.lossPct ?? 0) >= 5 || (s.rttMs ?? 0) >= 400) return "weak";
  if (s.quality === "unknown" && s.packetsSentDelta === null) return "unknown";
  return "good";
}

export interface MicSample {
  published: boolean;
  muted: boolean;
  /** Latest level 0-1, or null when it cannot be measured. */
  level: number | null;
  /** Milliseconds since the mic last produced sound above SILENT_LEVEL. */
  quietForMs: number;
}

export function micState(s: MicSample): MicState {
  if (!s.published) return "off";
  if (s.muted) return "muted";
  if (s.level !== null && s.quietForMs >= SILENT_AFTER_MS) return "silent";
  return "live";
}

export interface ReceivedReceipt {
  receipt: VoiceReceipt;
  at: number;
}

export function listenerState(
  me: string,
  receipt: ReceivedReceipt | undefined,
  { now, unmutedSince }: { now: number; unmutedSince: number | null },
): ListenerState {
  if (!receipt || now - receipt.at > RECEIPT_TTL_MS || unmutedSince === null) return "unknown";
  const settling = now - unmutedSince < UNMUTE_GRACE_MS;
  if (receipt.receipt.missing.includes(me)) {
    // Audio takes a moment to start flowing after an unmute.
    return settling ? "unknown" : "missing";
  }
  // A note from before the unmute says nothing about now -- until the grace
  // is over, by when a listener who is not receiving would have said so.
  if (settling && receipt.at < unmutedSince) return "unknown";
  if (!receipt.receipt.playing) return "paused";
  // "Allowed to play" is not "heard": the listener measured nothing audible.
  return receipt.receipt.inaudible?.includes(me) ? "inaudible" : "hears";
}

export interface ListenerView {
  identity: string;
  name: string;
  state: ListenerState;
}

export interface VoicePath {
  mic: MicState;
  micLevel: number | null;
  network: NetworkState;
  lossPct: number | null;
  rttMs: number | null;
  quality: ConnectionQualityValue;
  listeners: ListenerView[];
}

export type VoicePathTone = "good" | "warn" | "bad" | "idle";

export interface VoicePathSummary {
  tone: VoicePathTone;
  /** Signal bars, 0-3, from the network checkpoint. */
  bars: number;
  /** Listeners who hear you, out of those who reported. */
  heardBy: number;
  reported: number;
}

export function summarize(path: VoicePath): VoicePathSummary {
  const bars = { good: 3, weak: 2, down: 0, unknown: 0 }[path.network] - (path.quality === "poor" ? 1 : 0);
  const reported = path.listeners.filter((l) => l.state !== "unknown").length;
  const heardBy = path.listeners.filter((l) => l.state === "hears").length;
  const sending = path.mic === "live" || path.mic === "silent";

  let tone: VoicePathTone;
  if (path.network === "down") tone = "bad";
  else if (!sending) tone = "idle";
  else if (path.listeners.some((l) => l.state === "missing" || l.state === "inaudible")) tone = "bad";
  else if (
    path.network === "weak" ||
    path.mic === "silent" ||
    path.listeners.some((l) => l.state === "paused")
  ) {
    tone = "warn";
  } else tone = "good";

  return { tone, bars: Math.max(0, bars), heardBy, reported };
}

/** Percent of packets lost between two samples of cumulative counters. */
export function lossBetween(
  prev: { sent: number; lost: number } | null,
  next: { sent: number; lost: number },
): number | null {
  if (!prev) return null;
  const sent = next.sent - prev.sent;
  const lost = Math.max(0, next.lost - prev.lost);
  if (sent <= 0) return null;
  return Math.min(100, (lost / (sent + lost)) * 100);
}
